import React, { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import './BottomNav.css'

/**
 * Bottom nav bar, built entirely from MENU below.
 *
 * Design decisions (since 10 destinations can't all sit on one bar):
 *  - The 3 most-used flat links (Production Unit, Orders, Defect Management)
 *    + Edit Contact sit directly on the bar as tabs (2 left, 2 right).
 *  - "New Allotment" (already flagged `highlight: true`, i.e. the one
 *    quick-add action) sits in the center slot as a normal flat tab,
 *    same as the rest of the bar.
 *  - Everything else — the 3 groups (Voucher, Product, Processing) and the
 *    remaining flat links (Bewara as Designer, Reset Password) — lives in
 *    the rounded "•••" overflow popup, groups as accordions.
 *  - Nothing is duplicated between the bar and the popup.
 *
 * URLs: normal items -> /contact/dashboard/:id/<path>
 *       editRoute item -> /contact/edit/:id
 */

const MENU = [
    {
        type: 'group',
        label: 'Voucher',
        icon: 'fa fa-inr',
        items: [
            { label: 'Processing Voucher', icon: 'fa fa-inr', path: 'processingvoucher' },
            { label: 'Production Voucher', icon: 'fa fa-inr', path: 'productionvoucher' },
            { label: 'Advance Voucher', icon: 'fa fa-inr', path: 'advancevoucher' },
            { label: 'Salary Voucher', icon: 'fa fa-inr', path: 'salaryvoucher' },
            { label: 'Expense Voucher', icon: 'fa fa-inr', path: 'expensevoucher' },
        ],
    },
    { type: 'link', label: 'Production', icon: 'fa fa-building', path: 'productionunit' },
    {
        type: 'group',
        label: 'Product',
        icon: 'fa fa-cube',
        items: [
            { label: 'My Products', icon: 'fa fa-cube', path: 'productallotment' },
            { label: 'Given Products Stock', icon: 'fa fa-cube', path: 'productstock' },
        ],
    },
    {
        type: 'group',
        label: 'Processing',
        icon: 'fa fa-recycle',
        items: [
            { label: 'Distribution', icon: 'fa fa-arrow-up', path: 'processingdistribution' },
            { label: 'Receiving', icon: 'fa fa-arrow-down', path: 'processingreceiving' },
            { label: 'Return', icon: 'fa fa-arrow-left', path: 'processingreturn' },
            { label: 'Given Processing Stock', icon: 'fa fa-recycle', path: 'processingstock' },
            { label: 'New Allotment', icon: 'fa fa-arrow-right', path: 'processingAllotment', highlight: true },
        ],
    },
    { type: 'link', label: 'Orders', icon: 'fa fa-level-up', path: 'orders' },
    { type: 'link', label: 'Defect', icon: 'fa fa-superpowers', path: 'defectmanagement' },
    { type: 'link', label: 'Bewara as Designer', icon: 'fa fa-shopping-basket', path: 'productdesign' },
    { type: 'link', label: 'Reset Password', icon: 'fa fa-key', path: 'resetpassword' },
    {
        type: 'group',
        label: 'Photo & Doc',
        icon: 'fa fa-camera',
        items: [
            { label: 'Upload Photo', icon: 'fa fa-camera', path: 'photo' },
            { label: 'Upload Document', icon: 'fa fa-camera', path: 'docs' },
        ],
    },
    { type: 'link', label: 'Edit Contact', icon: 'fa fa-pencil', editRoute: true },
]

// which flat links get a direct spot on the bar (2 left, 2 right of the center slot)
const LEFT_TAB_PATHS = ['productionunit', 'orders']
const RIGHT_TAB_PATHS = ['defectmanagement'] // + Edit Contact, added separately below

const COLOR_CYCLE = ['blue', 'violet', 'green', 'amber', 'teal']

function ItemLink({ item, className, children, onNavigate }) {
    if (item.to) {
        return (
            <Link
                to={item.to}
                class={className}
                onClick={(e) => {
                    if (item.onClick) item.onClick(e)
                    if (onNavigate) onNavigate()
                }}
            >
                {children}
            </Link>
        )
    }
    return (
        <button type="button" class={className} onClick={(e) => { item.onClick && item.onClick(e); onNavigate && onNavigate() }}>
            {children}
        </button>
    )
}

export function BottomNav() {
    const { id } = useParams()
    const location = useLocation()
    const [isMoreOpen, setIsMoreOpen] = useState(false)
    const [openGroups, setOpenGroups] = useState(new Set())
    const popupRef = useRef(null)

    useEffect(() => {
        if (!isMoreOpen) return
        function onDocClick(e) {
            if (popupRef.current && !popupRef.current.contains(e.target)) setIsMoreOpen(false)
        }
        function onKey(e) {
            if (e.key === 'Escape') setIsMoreOpen(false)
        }
        document.addEventListener('mousedown', onDocClick)
        document.addEventListener('keydown', onKey)
        return () => {
            document.removeEventListener('mousedown', onDocClick)
            document.removeEventListener('keydown', onKey)
        }
    }, [isMoreOpen])

    useEffect(() => {
        setIsMoreOpen(false)
    }, [location.pathname])

    function linkTo(item) {
        return item.editRoute ? `/contact/edit/${id}` : `/contact/dashboard/${id}/${item.path}`
    }

    function isActive(item) {
        return location.pathname === linkTo(item)
    }

    function toggleGroup(label) {
        setOpenGroups((prev) => {
            const next = new Set(prev)
            next.has(label) ? next.delete(label) : next.add(label)
            return next
        })
    }

    // ---- derive bar tabs, center slot and popup content from MENU ----
    const editItem = MENU.find((m) => m.editRoute)
    const leftTabs = MENU.filter((m) => m.type === 'link' && LEFT_TAB_PATHS.includes(m.path))
    const rightTabs = [
        ...MENU.filter((m) => m.type === 'link' && RIGHT_TAB_PATHS.includes(m.path)),
        editItem,
    ]
    const barPaths = new Set([...LEFT_TAB_PATHS, ...RIGHT_TAB_PATHS])
    const fabSource = MENU
        .find((m) => m.type === 'group' && m.label === 'Processing')
        .items.find((i) => i.path === 'processingAllotment')

    const popupMenu = MENU.filter((m) => {
        if (m.editRoute) return false
        if (m.type === 'link') return !barPaths.has(m.path)
        return true // groups always shown in popup (in full, including New Allotment as a normal row too)
    })

    return (
        <>
            <div class={"bn-scrim" + (isMoreOpen ? " is-open" : "")} onClick={() => setIsMoreOpen(false)}></div>

            {isMoreOpen && (
                <div class="bn-more-popup" ref={popupRef} role="menu">
                    <div class="bn-more-popup-handle"></div>
                    <ul class="bn-more-list">
                        {popupMenu.map((entry, i) => {
                            const color = COLOR_CYCLE[i % COLOR_CYCLE.length]

                            if (entry.type === 'link') {
                                const active = isActive(entry)
                                return (
                                    <li key={entry.label}>
                                        <Link
                                            to={linkTo(entry)}
                                            class={"bn-more-row" + (active ? " is-active" : "")}
                                            onClick={() => setIsMoreOpen(false)}
                                        >
                                            <span class={"bn-more-icon bn-color-" + color}>
                                                <i class={entry.icon}></i>
                                            </span>
                                            <span class="bn-more-text">
                                                <span class="bn-more-title">{entry.label}</span>
                                            </span>
                                            <i class="fa fa-chevron-right bn-more-chevron"></i>
                                        </Link>
                                    </li>
                                )
                            }

                            const isOpen = openGroups.has(entry.label)
                            const groupHasActive = entry.items.some(isActive)
                            return (
                                <li key={entry.label} class={"bn-group" + (isOpen ? " is-open" : "")}>
                                    <button
                                        type="button"
                                        class={"bn-more-row bn-group-header" + (groupHasActive ? " is-active" : "")}
                                        onClick={() => toggleGroup(entry.label)}
                                        aria-expanded={isOpen}
                                    >
                                        <span class={"bn-more-icon bn-color-" + color}>
                                            <i class={entry.icon}></i>
                                        </span>
                                        <span class="bn-more-text">
                                            <span class="bn-more-title">{entry.label}</span>
                                        </span>
                                        <i class="fa fa-chevron-down bn-group-caret"></i>
                                    </button>

                                    <ul class="bn-sub-list">
                                        {entry.items.map((sub) => {
                                            const active = isActive(sub)
                                            return (
                                                <li key={sub.label}>
                                                    <Link
                                                        to={linkTo(sub)}
                                                        class={
                                                            "bn-sub-row" +
                                                            (active ? " is-active" : "") +
                                                            (sub.highlight ? " is-highlight" : "")
                                                        }
                                                        onClick={() => setIsMoreOpen(false)}
                                                    >
                                                        <i class={sub.icon}></i>
                                                        <span>{sub.label}</span>
                                                    </Link>
                                                </li>
                                            )
                                        })}
                                    </ul>
                                </li>
                            )
                        })}
                    </ul>
                </div>
            )}

            <nav class="bn-dock" aria-label="Bottom navigation">
                <div class="bn-dock-bar">
                    {leftTabs.map((item) => (
                        <ItemLink
                            key={item.label}
                            item={{ to: linkTo(item) }}
                            className={"bn-tab" + (isActive(item) ? " is-active" : "")}
                        >
                            <i class={item.icon}></i>
                            <span>{item.label}</span>
                        </ItemLink>
                    ))}


                    {rightTabs.map((item) => (
                        <ItemLink
                            key={item.label}
                            item={{ to: linkTo(item) }}
                            className={"bn-tab" + (isActive(item) ? " is-active" : "")}
                        >
                            <i class={item.icon}></i>
                            <span>{item.label}</span>
                        </ItemLink>
                    ))}

                    <button
                        type="button"
                        class={"bn-tab bn-more-btn" + (isMoreOpen ? " is-active" : "")}
                        onClick={() => setIsMoreOpen((o) => !o)}
                        aria-expanded={isMoreOpen}
                        aria-label="More options"
                    >
                        <i class="fa fa-ellipsis-h"></i>
                        <span>Menu</span>
                    </button>
                </div>
            </nav>
        </>
    )
}